import { useCallback, useEffect, useRef } from 'react';
import { queueOrderTrackingWrite } from '../helpers/orderTracking';
import { getSessionOrderAccessToken } from '../common/common';
import { tfnDigits } from '../helpers/trackingAttrs';

export const ORDER_ID_POLL_MS = 1000;
export const ORDER_ID_MAX_TICKS = 60; // 60s, well past any create round trip

/**
 * Record the toll-free number currently on screen as `order.trackingAttrs.displayedTFN`
 * (ELNKWEB-3215).
 *
 * Pure observer: it never influences which number is displayed, it only reports
 * the one that is. Every trigger the ticket names - address change, serviceability
 * result change, Invoca flip, DNI resolution - already re-derives the caller's
 * value, so this hook needs no change detection of its own beyond a dedupe.
 *
 * Kept byte-identical with the copies in the sibling checkouts -
 * app-checkout.earthlink.net and app-checkout.earthlink.com - so diff them before
 * and after editing this file. A change here that lands in only one site is how
 * the three drifted apart in the first place. Rationale and the full contract:
 * ELNKWEB-3215.
 *
 * @param {string} displayedValue the site's authoritative on-screen number, or
 *        falsy when no number is rendered.
 */
const useDisplayedTfnTracking = (displayedValue) => {
  // Last value handed to the writer. Set at enqueue time rather than on render,
  // so a re-render with the same number cannot produce a second write.
  const lastSentRef = useRef('');
  // Value waiting for an order id to exist. The id arrives from the create call,
  // which can resolve after the number is already rendered.
  const awaitingOrderIdRef = useRef('');
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const send = useCallback((digits, orderId) => {
    const previouslySent = lastSentRef.current;
    // Optimistic: claim the value now so a re-render while this write is still in
    // flight cannot enqueue the same number twice.
    lastSentRef.current = digits;
    // Serialized against every other tracking writer, not just this hook's own
    // previous write: the backend merges read-then-write without a lock, so two
    // overlapping PUTs drop one another's keys. The queue also bypasses redux
    // entirely, so a tracking write can never reach the reducer state checkout
    // renders - see the comment on putOrderTracking.
    Promise.resolve(queueOrderTrackingWrite({ displayedTFN: digits }, orderId))
      .then((written) => {
        // Give the number back to the dedupe when the write did NOT land.
        // Claiming it unconditionally meant one transient failure suppressed that
        // number for the rest of the session: the effect only re-runs on a CHANGED
        // value, and the header re-resolving to the same number is not a change.
        // With the rollback, the ordinary '' -> number churn (the header hides the
        // TFN whenever redux `loading` is set) is enough to retry it. Skipped when
        // a newer value has been claimed since, so a rollback cannot resurrect a
        // stale number.
        if (!written && lastSentRef.current === digits) {
          lastSentRef.current = previouslySent;
        }
      })
      // The queue's contract is that it never rejects; belt-and-braces so a future
      // change there cannot turn a tracking write into an unhandled rejection in
      // the middle of checkout.
      .catch(() => {});
  }, []);

  useEffect(() => {
    const digits = tfnDigits(displayedValue);

    // Nothing rendered yet, or nothing numeric. Deliberately does NOT clear a
    // previously recorded number: an empty render is not evidence that the
    // customer stopped seeing one.
    if (!digits) {
      return undefined;
    }

    if (digits === lastSentRef.current) {
      return undefined;
    }

    const orderId = getSessionOrderAccessToken();
    if (orderId) {
      stopPolling();
      awaitingOrderIdRef.current = '';
      send(digits, orderId);
      return undefined;
    }

    // The order access token is written to sessionStorage by the create call, which is not
    // ordered against the header rendering a number. Nothing re-renders when it
    // lands, so without this the first displayed number would go unrecorded for
    // the whole session.
    awaitingOrderIdRef.current = digits;
    if (pollRef.current) {
      return undefined;
    }
    let ticks = 0;
    pollRef.current = setInterval(() => {
      ticks += 1;
      const id = getSessionOrderAccessToken();
      if (id) {
        const queued = awaitingOrderIdRef.current;
        awaitingOrderIdRef.current = '';
        stopPolling();
        if (queued && queued !== lastSentRef.current) {
          send(queued, id);
        }
      } else if (ticks >= ORDER_ID_MAX_TICKS) {
        // No order was ever created (a flow that never calls create, or a failed
        // one that has already redirected). Stop rather than poll forever.
        stopPolling();
      }
    }, ORDER_ID_POLL_MS);

    return undefined;
  }, [displayedValue, send, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);
};

export default useDisplayedTfnTracking;
